import React, { useEffect, useState } from "react";
import { Table, Input, Tag, Space, Popconfirm, DatePicker } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteLab,
  fetchAllLabs,
  setLabDetailsIsEdit,
  fetchLabUsersMap,
} from "./redux/actions";
import SpinnerComponent from "../../components/spinner";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import CustomPagination from "../../components/customPagination/CustomPagination";
import moment from "moment";
import CustomCalendar from "../../components/calendar";
import AMultiSelect from "../../components/AMultiSelect";

const LabsRegisterTable = ({ canUpdate, canDelete }) => {
  const [sortedInfo, setSortedInfo] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [currentDelRecord, setCurrentDelRecord] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(() => 1);
  const [resultPerPage, setResultPerPage] = useState(() => 10);
  const [req, setReq] = useState(false);
  const [search, setSearch] = useState({});
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [statusCheckedList, setStatusCheckedList] = useState([]);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [date, setDate] = useState(null);
  const [labStatuses, SetLabStatuses] = useState([]);

  const {
    allLabs,
    fetchLabsLoading,
    deleteLabLoading,
    totalRecords,
    labUsersMap,
  } = useSelector((state) => state.labsSetupReducer);

  const { allLabStatuses } = useSelector(
    (state) => state.labStatusesReducer
  );

  const getSalesRepsName = (record) => {
    const assignedUsers = labUsersMap?.[record.id] || [];
    if (Array.isArray(assignedUsers) && assignedUsers.length > 0) {
      return assignedUsers
        .map((el) => {
          const { user } = el;
          return `${user?.firstName || ""} ${user?.lastName || ""}`;
        })
        .join("\n");
    }
    return record.salesRep || "";
  };

  useEffect(() => {
    let updatedStatused = allLabStatuses.map((el) => {
      const { statusName } = el;
      return { value: statusName, label: statusName };
    });
    SetLabStatuses(updatedStatused);
  }, [allLabStatuses]);

  useEffect(() => {
    if (page && resultPerPage && page > 0 && resultPerPage > 0) {
      const isSalesClientSort = sortedInfo?.columnKey === "salesRep";

      // Filter out salesRepTerm from search before sending to server
      const { salesRepTerm, ...serverSearch } = search;

      dispatch(
        fetchAllLabs({
          page: page,
          perPage: resultPerPage,
          search: serverSearch,
          sortBy: isSalesClientSort
            ? null
            : sortedInfo?.order && sortedInfo?.columnKey,
          sortOrder: isSalesClientSort
            ? null
            : sortedInfo?.order
              ? sortedInfo?.order === "ascend"
                ? "asc"
                : "desc"
              : null,
          showByDefault: true,
        })
      );
    }
  }, [req]);

  useEffect(() => {
    let paramsPage = 1;
    if (paramsPage && resultPerPage && paramsPage > 0 && resultPerPage > 0) {

      // Filter out salesRepTerm from search before sending to server
      const { salesRepTerm, ...serverSearch } = search;

      dispatch(
        fetchAllLabs({
          page: paramsPage,
          perPage: resultPerPage,
          search: serverSearch,
          showByDefault: true,
        })
      );
      setPage(1);
    }
  }, [defaultPageOneReq]);

  useEffect(() => {
    if (sortedInfo?.columnKey !== "salesRep") {
      debouncedCallApi();
    }
  }, [sortedInfo]);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 1000)
  );

  // Fetch users for loaded labs
  useEffect(() => {
    if (allLabs && allLabs.length > 0) {
      const idsToFetch = allLabs.map((lab) => lab.id);
      dispatch(fetchLabUsersMap(idsToFetch));
    }
  }, [allLabs]);

  // Update dataSource and apply client-side filtering for Sales Rep
  useEffect(() => {
    let data = allLabs ? [...allLabs] : [];

    if (search.salesRepTerm) {
      const term = search.salesRepTerm.toLowerCase();
      data = data.filter((record) => {
        const name = getSalesRepsName(record);
        return name.toLowerCase().includes(term);
      });
    }

    setDataSource(data);
  }, [allLabs, search.salesRepTerm, labUsersMap]);

  const handleSelectOnchange = (value, options, key, setHandler) => {
    if (value.includes("all")) {
      setHandler([...options.map((item) => item.value)]);
      value = options.map((item) => item.value);
    } else if (value.includes("not-all")) {
      setHandler([]);
      value = [];
    } else {
      setHandler(value);
    }
    handleMultiSelect(value, key);
  };

  const handleMultiSelect = (list, key) => {
    setSearch((prevSearch) => {
      const newSearch = { ...prevSearch };

      // If list is empty, remove the key entirely
      if (!list || list.length === 0) {
        delete newSearch[key === "status" ? "status" : key];
      } else {
        // For status, send the status name(s) directly
        newSearch[key === "status" ? "status" : key] = list;
      }

      return newSearch;
    });
  };

  const handleChange = (pagination, filters, sorter) => {
    if (datePickerOpened) {
      return;
    }
    setSortedInfo(sorter);
  };

  const handleDelete = (record) => {
    setCurrentDelRecord(record);
    dispatch(deleteLab(record));
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        setSearch({
          ...search,
          [key]: e.target.value,
        });
        debouncedDefaultPageOneCallApi();
      }}
    />
  );

  const dateInput = (key) => (
    <DatePicker
      placeholder=""
      className="activities-time-picker prod-due-date"
      getPopupContainer={() =>
        document.getElementsByClassName(
          "ant-layout-content layout-content site-layout-background custom-textarea"
        )[0]
      }
      suffixIcon={null}
      format="D MMM YYYY"
      onChange={(e) => {
        setSearch({
          ...search,
          [key]: e,
        });
        debouncedDefaultPageOneCallApi();
      }}
      onOpenChange={(e) => setDatePickerOpened(e)}
    />
  );

  const handleNavigate = (id) => navigate(`/labs-setup/update-lab/${id}`);

  const handleDateInput = (value) => {
    setDate(value);
    if (value === null) {
      setSearch({
        ...search,
        startDate: "",
        endDate: "",
      });
    } else {
      setSearch({
        ...search,
        startDate: value[0],
        endDate: value[1],
      });
    }
    debouncedDefaultPageOneCallApi();
  };

  const columns = [
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Lab Name</div>
            <div>{searchInput("labName")}</div>
          </div>
        );
      },
      dataIndex: "labName",
      key: "labName",
      // sorter: (a, b) => a.labName.localeCompare(b.labName),
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === "labName" && sortedInfo?.order,
      ellipsis: true,
      width: "25%",
      render: (labName, { id }) => (
        <div onClick={() => handleNavigate(id)}>{labName}</div>
      ),
    },

    {
      title: () => {
        return (
          <div
            align="top"
            style={{ width: "100%" }}
            className="select-parent-div"
          >
            <div className="special-table-header-text">Branch Code</div>
            <div>{searchInput("branchCode")}</div>
          </div>
        );
      },
      dataIndex: "branchCode",
      key: "branchCode",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "branchCode" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (branchCode, { id }) => (
        <div onClick={() => handleNavigate(id)}>
          {branchCode}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Sales Rep</div>
            <div>{searchInput("salesRepTerm")}</div>
          </div>
        );
      },
      dataIndex: "salesRep",
      key: "salesRep",
      sorter: (a, b) => {
        const nameA = getSalesRepsName(a).toLowerCase();
        const nameB = getSalesRepsName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      },
      sortOrder: sortedInfo?.columnKey === "salesRep" && sortedInfo?.order,
      ellipsis: true,
      width: "18%",
      render: (salesRep, record) => {
        const salesRepsName = getSalesRepsName(record);

        return (
          <div
            onClick={() => handleNavigate(record.id)}
            style={{ whiteSpace: "pre-line" }}
          >
            {salesRepsName}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Status</div>
            <div style={{ width: "100%" }}>
              <AMultiSelect
                dropdownClassName="status-selector"
                debouncedCallApi={debouncedCallApi}
                disabled={false}
                placeholder=""
                selectTags={statusCheckedList}
                onSelectChange={(value) =>
                  handleSelectOnchange(
                    value,
                    labStatuses,
                    "status",
                    setStatusCheckedList
                  )
                }
                data={labStatuses}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "statusName",
      key: "statusName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "statusName" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      align: "center",
      render: (status, { labStatus, id }) => {
        const { statusName } = labStatus;
        let tagColor = labStatus.color || "#d9d9d9";

        if (statusName?.toLowerCase() === "active") {
          tagColor = "#a7e0a5";
        } else if (statusName?.toLowerCase() === "inactive") {
          tagColor = "#fcb9b8";
        }

        return (
          <Tag
            onClick={() => handleNavigate(id)}
            color={tagColor}
            style={{
              color: "#383838",
              width: "70px",
              height: "20px",
              borderRadius: "10px",
              textAlign: "center",
              fontSize: "12px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {statusName}
          </Tag>
        );
      },
    },
    {
      title: null,
      dataIndex: "",
      key: "action",
      width: "8%",
      align: "center",
      render: (_, record) => {
        if (!canUpdate && !canDelete) {
          return;
        }
        return deleteLabLoading && record.id === currentDelRecord.id ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={15} align="baseline">
            {canUpdate && (
              <EditIcon
                width={18}
                height={18}
                className="custom-icon"
                onClick={() => {
                  handleNavigate(record.id);
                  dispatch(setLabDetailsIsEdit(true));
                }}
              />
            )}
            {canDelete && (
              <Popconfirm
                title="Are you sure you want to delete this Lab?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <DeleteIcon width={18} height={18} className="custom-icon" />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className="special-table lab-register-table"
        columns={columns}
        scroll={{ x: !fetchLabsLoading }}
        onChange={handleChange}
        dataSource={dataSource}
        pagination={false}
        sortUpIcon={<UpOutlined />}
        sortDownIcon={<DownOutlined />}
        loading={fetchLabsLoading}
      />
      <CustomPagination
        page={page}
        setPage={setPage}
        resultPerPage={resultPerPage}
        setResultPerPage={setResultPerPage}
        debouncedCallApi={debouncedCallApi}
        totalRecord={totalRecords}
      />
    </>
  );
};

export default LabsRegisterTable;

